import React, { useEffect, useState, useCallback, useMemo } from 'react';
import SimpleTableViewer from '../SimpleTable';
import SpendCardViewer from '../Spend/SpendCard';
import { checkConditions } from '../../TrackerViewer/utils';
import useStyles from './style';

function LinkedTables(props) {
    const classes = useStyles();
    const { tabDef, blockState = {} } = props;

    /** Table columns: same rules as StepLevelBlocks / CardInfo — show only when `conditions` match blockState */
    const visibleSpendColumns = useMemo(() => {
        const els = tabDef?.spendInformation?.dataElement;
        if (!Array.isArray(els)) return [];
        return els.filter((item) => checkConditions(item?.conditions, blockState));
    }, [tabDef?.spendInformation?.dataElement, blockState]);


    const [tableData, setTableData] = useState({});
    const [cardData, setCardData] = useState({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sort, setSort] = useState({});
    const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);

    const handlePageChange = useCallback((event, newPage) => {
        setPage(newPage);
    }, []);

    const handleRowsPerPageChange = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    const handleSortChange = useCallback((newSort) => {
        setSort(newSort);
    }, []);

    const handleSingleRowDownload = useCallback((e, rowData) => {
        const rowId = rowData?.id || rowData?._id;
        if (!rowId) {
            console.warn('No document ID found in row data');
            return;
        }
        const fieldList = visibleSpendColumns.length
            ? visibleSpendColumns.map((i) => i.value).join(',')
            : (tabDef?.spendInformation?.dataElement || []).map((i) => i.value).join(',');
        let payload = {
            dataModelId: tabDef.dataModelId,
            appid: props.match.params.appid,
            fields: fieldList,
            downloadType: 'excel',
            ids: [rowId],
            dataUniqueId: props.data.chartUniqueId + '_' + 'single_row' + '_download'
        }
        props.downloadSheet(payload);
    }, [visibleSpendColumns, tabDef, props.match?.params?.appid, props.data?.chartUniqueId, props.downloadSheet]);

    const fetchData = useCallback(() => {
        if (!props.getSpendDetails || !tabDef?.dataModelId || !props.match?.params?.id) return;
        props.getSpendDetails({
            appId: props.match.params.appid,
            dataModelId: tabDef.dataModelId,
            mainDataModelId: tabDef.mainDataModelId,
            documentId: props.match.params.id,
            dataUniqueId: props.data?.chartUniqueId + '_' + props.activeTabMain + '_tableData',
            field: tabDef?.field || 'documentId',
            ...(tabDef?.combineFieldsIntoSingleField && { combineFieldsIntoSingleField: true }),
            sort: sort,
            payload: {
                dataModelId: tabDef.dataModelId,
                mainDataModelId: tabDef.mainDataModelId,
                documentId: props.match.params.id,
                field: tabDef?.field || 'documentId',
                ...(tabDef?.combineFieldsIntoSingleField && { combineFieldsIntoSingleField: true }),
                sort: sort,
                page: page,
                rowsPerPage: rowsPerPage
            }
        });
    }, [tabDef, props.match?.params?.id, sort, page, rowsPerPage]);

    useEffect(() => {
        if (props.activeTabMain.includes('_linked_tables')) {
            fetchData();
        }
    }, [props.activeTabMain, fetchData]);

    useEffect(() => {
        const dataKey = props.data?.chartUniqueId + '_' + props.activeTabMain + '_tableData';
        if (dataKey && props.formData?.document?.[dataKey]) {
            const responseData = props.formData.document[dataKey] || {};
            setTableData(responseData);
            Object.keys(responseData).forEach(key => {
                const dataElement = tabDef?.uploadCard?.dataElement?.find(item => item.value === key);
                if (dataElement) {
                    setCardData(prev => ({
                        ...prev,
                        [key]: responseData[dataElement.value] || ''
                    }));
                }
            });
        }
    }, [props.formData?.document?.[props.data?.chartUniqueId + '_' + props.activeTabMain + '_tableData']]);

    if (!tabDef) return null;

    return (
        <div className={classes.root}>
            {tabDef?.uploadCard?.dataElement && (
                <SpendCardViewer
                    {...props}
                    data={tabDef?.uploadCard?.dataElement}
                    blockState={cardData}
                    isLoading={props.formData?.[props.data?.chartUniqueId + '_' + props.activeTabMain + '_tableData_loading']}
                />
            )}
            {tabDef?.isCustomType?.enable && (
                <div className={classes.tableHost}>
                    <SimpleTableViewer
                        {...props}
                        title={tabDef?.title}
                        dataObj={tableData?.data}
                        selectedDataElement={[]}
                        dataModel={tabDef?.dataModel}
                        selectedTableDataElement={visibleSpendColumns}
                        isActionEnabled={tabDef?.spendInformation?.actions?.enable}
                        isObjectTable={false}
                        pagination={true}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        totalRecords={tableData?.totalRecords}
                        onDownload={handleSingleRowDownload}
                        isDownloadButton={true}
                        isCheckbox={false}
                        sort={sort}
                        onSortChange={handleSortChange}
                        onCheckboxChange={(updatedSelection) => setSelectedDocumentIds(updatedSelection)}
                        selectedRows={selectedDocumentIds}
                    />
                </div>
            )}
        </div>
    );
}

export default LinkedTables;